import {
  logisticsKnowledgeTypes,
  storeKnowledgeTypes,
  useLLM,
} from '@knowledge/common/hooks/useGroup';
import { find } from 'lodash-es';
import { useCallback, useRef } from 'react';
import isEqual from 'react-fast-compare';
import {
  combineStoreScopes,
  defaultValueMap,
  formValue2knowledgeMap,
  knowledge2formValueMap,
  knowledgeTypeStepMap,
  stores2storeScopes,
  UnConfigStore,
} from 'views/BootGuide/views/AiModel/share';
import { useAreaLabel } from 'views/BootGuide/views/AiModel/TransitDays';
import { useAreaLabel as useShipPlaceAreaLabel } from 'views/BootGuide/views/AiModel/ShipPlace';
import { groupBy } from './edit/hook';
import { newCondition, newKnowledge } from './edit/knowledge';
import { useMemoizedFn } from 'ahooks';
import { StoreGroupWithScopes, storeIds2stores } from './StoreGroup/share';
import { Store } from 'components/StoreScopes';
import {
  getShippingDaysDefaultValues,
  getShipPlaceDefaultValues,
  getTransitDaysDefaultValues,
} from 'views/BootGuide/views/AiModel/Logistics';

const needSliceStoreKnowledgeTypes: Knowledge.KnowledgeType[] = [
  'LOGISTICS_SEND_OUT_TIME',
  'LOGISTICS_DELIVERED_IN_TIME',
  'LOGISTICS_SEND_OUT_PLACE',
];

/**
 * 需要进行拆分店铺（店铺类型）的知识类型
 * @param type
 */
export const isNeedSliceStoreKnowledgeType = (type: Knowledge.KnowledgeType) =>
  needSliceStoreKnowledgeTypes.includes(type);

export type RegionTypeStoresMap = Partial<
  Record<General.UpperCaseRegion, Partial<Record<'cross' | 'local' | '3pf', UnConfigStore[]>>>
>;

export const getStoreMap = <T extends { id: number }>(stores: T[]) =>
  stores.reduce<Partial<Record<number, T>>>((res, store) => {
    res[store.id] = store;
    return res;
  }, {});
export const getExternalStoreMap = (stores: UnConfigStore[]) =>
  stores.reduce<Partial<Record<string, UnConfigStore>>>((res, store) => {
    res[store.externalId] = store;
    return res;
  }, {});
export const getRegionTypeStoresMap = (stores: UnConfigStore[]) => {
  return stores.reduce<RegionTypeStoresMap>((res, store) => {
    const { region, is3pf, storeType } = store;
    const type = is3pf ? '3pf' : storeType === 'CROSS' ? 'cross' : 'local';
    if (!res[region]) {
      res[region] = {
        [type]: [store],
      };
    } else {
      if (!res[region][type]) {
        res[region][type] = [store];
      } else {
        res[region][type].push(store);
      }
    }
    return res;
  }, {});
};

const knowledgeTypeValuesMap: Knowledge.KnowledgeTypeValuesMap = {
  LOGISTICS_SEND_PICTURES_BEFORE: ['noSupport', 'uncertain', 'support'],
  SHOP_INVOICE: ['none', 'invoiceAndReceipt', 'receipt', 'invoice'],
  SHOP_PICKUP_AT_HOME: ['noSupport', 'support'],
  SHOP_FREE_SHIPPING: [
    'FREE_SHIPPING',
    'NO_FREE_SHIPPING',
    'FREE_SHIPPING_BY_PLATFORM_SEND_COUPON',
  ],
  LOGISTICS_DELIVERY_COURIER_TYPE: ['noSupport', 'support'],
  LOGISTICS_CONSOLIDATED_MULTIPLE_ORDER: ['noSupport', 'uncertain', 'support'],
  ITEM_WARRANTY_SERVICE: ['noSupport'],
  OTHER_GIFT: ['NO_GIFT', 'ALL_GIFT'],
  SHOP_WHOLESALE: ['noSupport', 'support'],
};

export function filterStoreScopes(
  storeScopes: General.StoreScope[],
  storeMap: Partial<Record<number, any>>,
) {
  return storeScopes
    .map(({ regionStoreScopes, ...others }) => {
      const nextRegionStoreScopes = regionStoreScopes
        .map(({ storeIds, storeCount, ...others }) => {
          const nextStoreIds = storeIds?.filter((id) => storeMap[id]);
          return {
            ...others,
            storeIds: nextStoreIds,
            storeCount: nextStoreIds?.length || storeCount,
          };
        })
        .filter(({ storeIds }) => storeIds?.length);
      return {
        ...others,
        storeCount: nextRegionStoreScopes.reduce((res, { storeCount }) => res + storeCount, 0),
        regionStoreScopes: nextRegionStoreScopes,
      };
    }, [])
    .filter(({ regionStoreScopes }) => regionStoreScopes.length);
}

export function useInitialValues() {
  const LLM = useLLM();

  const { t, getAreaLabel } = useAreaLabel();
  const { getAreaLabel: getShipPlaceAreaLabel } = useShipPlaceAreaLabel();

  const refsCurrent = { getAreaLabel, getShipPlaceAreaLabel };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  const groupByStoreGroup = useCallback(
    ({
      type,
      storeGroupList,
      storeMap: allStoreMap,
      knowledgeValues,
    }: {
      type: Knowledge.KnowledgeType;
      storeGroupList: StoreGroupWithScopes[];
      storeMap: Partial<Record<number, UnConfigStore>>;
      knowledgeValues?: any[];
    }) => {
      return storeGroupList.reduce<any[]>((res, storeGroup) => {
        const { storeIds } = storeGroup;
        const stores = storeIds2stores(storeIds, allStoreMap);
        const storeMap = getStoreMap(stores);
        const externalStoreMap = getExternalStoreMap(stores);
        const knowledges = (knowledgeValues || []).reduce<any[]>((res, knowledge) => {
          const { itemScope, storeScope, content, templateCode, ...others } = knowledge;
          const filteredStoreScopes = filterStoreScopes(storeScope.storeScope, storeMap);
          if (!filteredStoreScopes.length) {
            return res;
          }
          const adaptValue = knowledge2formValueMap[type](filteredStoreScopes, {
            templateCode,
            value: content,
            saleTypes: itemScope.saleTypes,
          });
          if (!adaptValue) {
            return res;
          }
          if (type === 'LOGISTICS_SEND_OUT_PLACE') {
            const customAreas = (adaptValue as any).areas?.filter(
              (area: any) => !refs.current.getShipPlaceAreaLabel(area),
            );
            if (customAreas?.length) {
              (adaptValue as any).customAreas = customAreas;
            }
          }
          if (type === 'LOGISTICS_DELIVERED_IN_TIME') {
            const customAreas = (adaptValue as any).areas
              ?.map(({ areas }: any) => areas)
              .flat()
              .filter((area: any) => !refs.current.getAreaLabel(area));
            if (customAreas?.length) {
              (adaptValue as any).customAreas = customAreas;
            }
          }
          const itemsFilter = (items: Knowledge.SpecifiedItem[]) =>
            items.filter(({ shopId }) => externalStoreMap[shopId]);
          const filteredItemScope = {
            ...itemScope,
            specifiedItems: itemsFilter(itemScope.specifiedItems),
            exclude: itemsFilter(itemScope.exclude),
          };
          res.push({
            condition: {
              ...others,
              content,
              templateCode,
              itemScope: filteredItemScope,
              storeScope: {
                storeType: 'ALL',
                storeScope: filteredStoreScopes,
              },
            },
            content,
            templateCode,
            adaptValue,
          });
          return res;
        }, []);
        if (type !== 'LOGISTICS_SEND_OUT_TIME') {
          res.push({
            storeGroup,
            stores,
            storeMap,
            externalStoreMap,
            knowledges,
          });
          return res;
        }
        const combinedKnowledges = knowledges.reduce<any[]>((res, knowledge) => {
          const { condition, adaptValue } = knowledge;
          if (adaptValue.saleType === 'all') {
            res.push({
              ...knowledge,
              adaptValue: {
                first: adaptValue,
              },
            });
            return res;
          }
          const { content, templateCode, itemScope, dtScope, id, ...conditionOthers } = condition;
          const { saleTypes, ...itemScopeOthers } = itemScope;
          const target = res.find((item: any) => {
            if (
              item.adaptValue.preSale ||
              item.adaptValue.first.saleType === 'all' ||
              item.adaptValue.first.saleType === adaptValue.saleType
            ) {
              return false;
            }
            const {
              content,
              templateCode,
              itemScope,
              dtScope: itemDtScope,
              id,
              ...itemConditionOthers
            } = item.condition;
            const { saleTypes, ...itemItemScopeOthers } = itemScope;
            return (
              isEqual(conditionOthers, itemConditionOthers) &&
              isEqual(itemScopeOthers, itemItemScopeOthers) &&
              ((dtScope.filterCode === 'DT_ALL' && itemDtScope.filterCode === 'DT_ALL') ||
                isEqual(dtScope, itemDtScope))
            );
          });
          if (!target) {
            res.push({
              ...knowledge,
              [`${adaptValue.saleType}Id`]: id,
              adaptValue: {
                first: adaptValue,
              },
            });
            return res;
          }
          target.condition.itemScope.saleTypes = ['ONSALE', 'PREORDER'];
          if (adaptValue.saleType === 'inSale') {
            target.adaptValue = {
              first: adaptValue,
              preSale: target.adaptValue.first,
            };
            target.inSaleId = id;
          }
          if (adaptValue.saleType === 'preSale') {
            target.adaptValue = {
              first: target.adaptValue.first,
              preSale: adaptValue,
            };
            target.preSaleId = id;
          }
          return res;
        }, []);
        res.push({
          storeGroup,
          stores,
          storeMap,
          externalStoreMap,
          knowledges: combinedKnowledges,
        });
        return res;
      }, []);
    },
    [],
  );

  const getKnowledgeInitialValues = useCallback(
    ({ type, knowledgeValues }: { type: Knowledge.KnowledgeType; knowledgeValues?: any[] }) => {
      return groupBy(knowledgeValues || [])
        .map((knowledgeValue) => {
          const { conditions, templateCode, content } = knowledgeValue;
          const storeScopes = combineStoreScopes(
            conditions.map((condition: any) => condition.storeScope.storeScope),
          );
          const adaptValue = knowledge2formValueMap[type](storeScopes, {
            templateCode,
            value: content,
          });
          if (adaptValue) {
            return {
              ...knowledgeValue,
              adaptValue,
            };
          }
          return undefined;
        })
        .filter((_) => _);
    },
    [],
  );

  const groupByValue = useCallback(
    ({ type, knowledgeValues }: { type: Knowledge.KnowledgeType; knowledgeValues?: any[] }) => {
      const createNewValue = (adaptValue: any) => {
        const { content, templateCode } = formValue2knowledgeMap[type](adaptValue) || {};
        const { conditions } = newKnowledge();
        return {
          conditions: conditions.map((condition: any) => {
            return {
              ...condition,
              content,
              templateCode,
            };
          }),
          content,
          templateCode,
          adaptValue,
        };
      };

      const knowledgeInitialValues = getKnowledgeInitialValues({ type, knowledgeValues });

      const generateShopDropShippingGroupValue = () => {
        const target1 = knowledgeInitialValues.find((values) => {
          return values.adaptValue.value === 'noSupport';
        });
        const target2 = knowledgeInitialValues.find((values) => {
          return (
            values.adaptValue.value === 'support' &&
            values.adaptValue.dropShippingType === 'SYSTEM_PROVIDES_TRACKING_NUMBER'
          );
        });
        const target3 = knowledgeInitialValues.find((values) => {
          return (
            values.adaptValue.value === 'support' &&
            values.adaptValue.dropShippingType === 'SYSTEM_NOT_PROVIDES_TRACKING_NUMBER'
          );
        });
        return [
          target1 ||
            createNewValue({
              value: 'noSupport',
              dropShippingType: 'SYSTEM_PROVIDES_TRACKING_NUMBER',
              storeScopes: [],
            }),
          target2 ||
            createNewValue({
              value: 'support',
              dropShippingType: 'SYSTEM_PROVIDES_TRACKING_NUMBER',
              storeScopes: [],
            }),
          target3 ||
            createNewValue({
              value: 'support',
              dropShippingType: 'SYSTEM_NOT_PROVIDES_TRACKING_NUMBER',
              storeScopes: [],
            }),
        ];
      };

      const generateLogisticsDeliveryCourierTypeGroupValue = () => {
        if (knowledgeInitialValues) {
          return knowledgeInitialValues;
        }
        const values = knowledgeTypeValuesMap['LOGISTICS_DELIVERY_COURIER_TYPE'];
        return values.map((value) =>
          createNewValue({
            value,
            deliveryTypes: [],
            storeScopes: [],
          }),
        );
      };

      const generateShopFreeShoppingGroupValue = () => {
        const values = knowledgeTypeValuesMap['SHOP_FREE_SHIPPING'];
        const findTarget = (value: any) =>
          knowledgeInitialValues.find((values) => values.adaptValue.freeShippingType === value);

        return values.map((value) => {
          const target = findTarget(value);
          if (target) {
            return target;
          }
          const adaptValue = {
            value,
            freeShippingType: value,
            storeScopes: [],
          };
          return createNewValue(adaptValue);
        });
      };

      const generateCommonGroupValue = () => {
        const values = knowledgeTypeValuesMap[type];
        const findTarget = (value: any) =>
          knowledgeInitialValues.find((values) => values.adaptValue.value === value);

        return values.map((value) => {
          const target = findTarget(value);
          if (target) {
            return target;
          }
          const adaptValue = {
            value,
            storeScopes: [],
          };
          return createNewValue(adaptValue);
        });
      };

      const generatePartialGroupValue = () => {
        const values = knowledgeTypeValuesMap[type];
        const noV = values.filter(
          (v) => !find(knowledgeInitialValues, (values) => values.adaptValue.value === v),
        );
        return knowledgeInitialValues.concat(
          noV.map((value) => {
            return createNewValue({
              value,
              storeScopes: [],
            });
          }),
        );
      };

      if (type === 'SHOP_DROPSHIPPING') {
        return generateShopDropShippingGroupValue();
      }
      if (type === 'LOGISTICS_DELIVERY_COURIER_TYPE') {
        return generateLogisticsDeliveryCourierTypeGroupValue();
      }
      if (type === 'SHOP_FREE_SHIPPING') {
        return generateShopFreeShoppingGroupValue();
      }
      if (type === 'ITEM_WARRANTY_SERVICE' || type === 'OTHER_GIFT') {
        return generatePartialGroupValue();
      }
      return generateCommonGroupValue();
    },
    [getKnowledgeInitialValues],
  );

  const getInitialValues = useCallback(
    ({
      type,
      storeGroupList,
      storeMap,
      knowledgeValues,
    }: {
      type: Knowledge.KnowledgeType;
      storeGroupList: StoreGroupWithScopes[];
      storeMap: Partial<Record<number, UnConfigStore>>;
      knowledgeValues?: any[];
    }) => {
      if (logisticsKnowledgeTypes.includes(type) && LLM) {
        if (LLM && isNeedSliceStoreKnowledgeType(type)) {
          return groupByStoreGroup({ type, storeGroupList, storeMap, knowledgeValues });
        }
        return groupByValue({ type, knowledgeValues });
      }
      if (storeKnowledgeTypes.includes(type)) {
        return groupByValue({ type, knowledgeValues });
      }
      return getKnowledgeInitialValues({ type, knowledgeValues });
    },
    [LLM, groupByStoreGroup, groupByValue, getKnowledgeInitialValues],
  );

  return {
    getInitialValues,
    LLM,
    t,
  };
}

export function useTransformKnowledges({ type }: { type: Knowledge.KnowledgeType }) {
  return useMemoizedFn((knowledges: any[]) => {
    return knowledges.reduce<any[]>((res, knowledge) => {
      const {
        adaptValue,
        condition: { id, content, templateCode, storeScope, dtScope, ...otherCondition },
      } = knowledge;
      if (type === 'LOGISTICS_SEND_OUT_TIME') {
        const pushKnowledge = (value: any) => {
          const contentValue = formValue2knowledgeMap[type](value);
          res.push({
            ...otherCondition,
            ...contentValue,
            storeScope: {
              storeType: 'ALL',
              storeScope: value.storeScopes,
            },
            dtScope: {
              ...dtScope,
              activeDuration: {
                startAt: dtScope.activeDuration?.startAt || null,
                endAt: dtScope.activeDuration?.endAt || null,
              },
            },
            itemScope: {
              ...otherCondition.itemScope,
              saleTypes:
                value.saleType === 'all'
                  ? ['ONSALE', 'PREORDER']
                  : value.saleType === 'inSale'
                  ? ['ONSALE']
                  : ['PREORDER'],
            },
          });
        };
        if (adaptValue.first) {
          pushKnowledge(adaptValue.first);
        }
        if (adaptValue.preSale) {
          pushKnowledge(adaptValue.preSale);
        }
      } else {
        const contentValue = formValue2knowledgeMap[type](adaptValue);
        if (contentValue) {
          res.push({
            ...otherCondition,
            ...contentValue,
            storeScope: {
              storeType: 'ALL',
              storeScope: adaptValue.storeScopes,
            },
            dtScope: {
              ...dtScope,
              activeDuration: {
                startAt: dtScope.activeDuration?.startAt || null,
                endAt: dtScope.activeDuration?.endAt || null,
              },
            },
          });
        }
      }
      return res;
    }, []);
  });
}

export const supportKnowledgePlatformRegions: Partial<
  Record<General.PlatformUpperCase, General.UpperCaseRegion[]>
> = {
  SHOPEE: [
    'ID',
    'MY',
    'PH',
    'TH',
    'VN',
    'SG',
    'BR',
    'MX',
    'DE',
    'IT',
    'JP',
    'IE',
    'ES',
    'FR',
    'CL',
    'CO',
    'AR',
  ],
  LAZADA: [
    'ID',
    'MY',
    'PH',
    'TH',
    'VN',
    'SG',
    'MX',
    'DE',
    'IT',
    'JP',
    'IE',
    'ES',
    'FR',
    'CL',
    'CO',
    'AR',
  ],
  TIKTOK: [
    'ID',
    'MY',
    'PH',
    'TH',
    'VN',
    'SG',
    'US',
    'GB',
    'MX',
    'BR',
    'DE',
    'IT',
    'JP',
    'IE',
    'ES',
    'FR',
    'CL',
    'CO',
    'AR',
  ],
  TIKTOK_US: [
    'ID',
    'MY',
    'PH',
    'TH',
    'VN',
    'SG',
    'US',
    'GB',
    'MX',
    'BR',
    'DE',
    'IT',
    'JP',
    'IE',
    'ES',
    'FR',
    'CL',
    'CO',
    'AR',
  ],
  MERCADO: [
    'ID',
    'MY',
    'PH',
    'TH',
    'VN',
    'SG',
    'US',
    'GB',
    'MX',
    'BR',
    'DE',
    'IT',
    'JP',
    'IE',
    'ES',
    'FR',
    'CL',
    'CO',
    'AR',
  ],
  KWAI: [
    'ID',
    'MY',
    'PH',
    'TH',
    'VN',
    'SG',
    'US',
    'GB',
    'MX',
    'BR',
    'DE',
    'IT',
    'JP',
    'IE',
    'ES',
    'FR',
    'CL',
    'CO',
    'AR',
  ],
};
export function supportKnowledgeStoresFilter<T extends Store>(stores: T[]) {
  return stores.filter(({ region, externalType }) =>
    supportKnowledgePlatformRegions[externalType]?.includes(region),
  );
}

export function useGetKnowledgeValues() {
  return useCallback(
    ({
      type,
      unConfigStores,
      defaultValueRule,
    }: {
      type: Knowledge.KnowledgeType;
      unConfigStores: UnConfigStore[];
      defaultValueRule?: BootGuide.AiModel.DefaultValueRule;
    }) => {
      const storeMap = getStoreMap(unConfigStores);
      const regionTypeStoresMap = getRegionTypeStoresMap(unConfigStores);
      const knowledgeValues = Object.keys(regionTypeStoresMap).reduce<any[]>((res, region) => {
        const typeStoresMap = regionTypeStoresMap[region];
        if (!typeStoresMap) {
          return res;
        }
        return Object.keys(typeStoresMap).reduce((res, storeType) => {
          const stores = typeStoresMap[storeType];
          if (!stores) {
            return res;
          }
          const storeScopes = stores2storeScopes(stores);
          const adaptValue: any =
            type === 'LOGISTICS_SEND_OUT_TIME'
              ? getShippingDaysDefaultValues(
                  storeType === '3pf' ? 'pf' : storeType,
                  storeScopes,
                  defaultValueRule,
                )
              : type === 'LOGISTICS_DELIVERED_IN_TIME'
              ? getTransitDaysDefaultValues(
                  region,
                  storeType === '3pf' ? 'pf' : storeType,
                  storeScopes,
                  defaultValueRule,
                )
              : type === 'LOGISTICS_SEND_OUT_PLACE'
              ? getShipPlaceDefaultValues(
                  region,
                  storeType === '3pf' ? 'pf' : storeType,
                  storeScopes,
                  defaultValueRule,
                )
              : {
                  ...defaultValueMap[knowledgeTypeStepMap[type]],
                  storeScopes,
                };
          const contentValue = formValue2knowledgeMap[type](
            type === 'LOGISTICS_SEND_OUT_TIME' ? adaptValue.first : adaptValue,
          );
          if (!contentValue) {
            return res;
          }
          const target = res.find(({ templateCode, content }) => {
            return templateCode === contentValue.templateCode && content === contentValue.content;
          });
          if (target) {
            target.storeScope.storeScope = combineStoreScopes([
              target.storeScope.storeScope,
              storeScopes,
            ]);
            return res;
          }
          res.push({
            definitionCode: type,
            ...contentValue,
            ...newCondition(),
            storeScope: {
              storeType: 'ALL',
              storeScope: storeScopes,
            },
          });
          return res;
        }, res);
      }, []);
      return {
        storeMap,
        knowledgeValues,
      };
    },
    [],
  );
}
